import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { KeywordGroupExternal, KeywordModalState } from "../../api/Blueprints";
import { useGetKeywordGroups } from "../../api/BlueprintsApi";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import { KeywordGroupButtonAdd, KeywordModal } from "./KeywordModal";
import KeywordRemove from "./KeywordRemove";
import { sortBy } from "lodash";

type Props = {
  blueprintUUID?: string;
  behaviorKitUUID?: string;
  buttonClassName?: string;
  readOnly?: boolean;
};

const KeywordSummary = ({
  blueprintUUID,
  behaviorKitUUID,
  buttonClassName,
  readOnly,
}: Props) => {
  const { mutate: getKeywords, data, isLoading } = useGetKeywordGroups();
  const [keywordToDelete, setKeywordToDelete] = useState<string>("");

  const validateAndGetKeyword = useCallback(() => {
    if (blueprintUUID) {
      getKeywords(blueprintUUID);
    } else if (behaviorKitUUID) {
      getKeywords(behaviorKitUUID);
    }
  }, [blueprintUUID, behaviorKitUUID, getKeywords]);

  useEffect(() => {
    validateAndGetKeyword();
  }, [getKeywords, blueprintUUID, behaviorKitUUID, validateAndGetKeyword]);

  function handleAfterSave() {
    validateAndGetKeyword();
  }

  const KeywordState = (keyword: KeywordGroupExternal): KeywordModalState => {
    const state: KeywordModalState = {
      includeExact: keyword.include_exact,
      includeFuzzy: keyword.include_fuzzy,
      excludeExact: keyword.exclude_exact,
      excludeFuzzy: keyword.exclude_fuzzy,
      name: keyword.name,
    };
    return state;
  };

  const sorted = sortBy(data, (p) => p.name.toLocaleLowerCase());

  return (
    <div className="p-2">
      {isLoading && <LoadingSpinner />}
      {sorted?.map((group) => (
        <div
          key={group.external_id}
          className="w-full flex items-center justify-center mt-1 rounded border p-2 text-sm"
        >
          <span className="font-bold truncate text-ellipsis max-w-xs flex-grow-0">
            {group.name}
          </span>
          <div className="w-96 ml-auto overflow-hidden text-sm">
            <div className="flex gap-4 items-center justify-center p-2 m-1">
              Included:
              <div className="w-40">
                <span className="rounded-md py-1 px-2 m-2 bg-green-100">
                  {group.include_exact.length} Exact Match
                </span>
              </div>
              <div className="w-40">
                <span className="rounded-md py-1 px-2 m-2 bg-green-100">
                  {group.include_fuzzy.length} Fuzzy Match
                </span>
              </div>
            </div>
            <div className="flex gap-4 items-center justify-center p-2 m-1">
              Excluded:
              <div className="w-40">
                <span className="rounded-md py-1 px-2 m-2 bg-yellow-100">
                  {group.exclude_exact.length} Exact Match
                </span>
              </div>
              <div className="w-40">
                <span className="rounded-md py-1 px-2 m-2 bg-yellow-100">
                  {group.exclude_fuzzy.length} Fuzzy Match
                </span>
              </div>
            </div>
          </div>
          {!readOnly && (
            <>
              <div className="flex gap-2 ml-2 text-gray-500 items-center">
                <PencilSquareIcon
                  className="w-4 cursor-pointer"
                  onClick={() => {
                    setKeywordToDelete(group.external_id);
                  }}
                />
                <KeywordRemove
                  onRemove={handleAfterSave}
                  keywordUUID={group.external_id}
                />
              </div>
              <KeywordModal
                showModal={keywordToDelete === group.external_id}
                close={() => setKeywordToDelete("")}
                blueprintUUID={blueprintUUID}
                behaviorKitIdUUID={behaviorKitUUID}
                keywordUUID={group.external_id}
                initialState={KeywordState(group)}
                onSave={handleAfterSave}
              />
            </>
          )}
        </div>
      ))}
      {!readOnly && (
        <div className="py-2">
          <KeywordGroupButtonAdd
            className={buttonClassName}
            blueprintUUID={blueprintUUID}
            behaviorKitUUID={behaviorKitUUID}
            onSave={handleAfterSave}
          />
        </div>
      )}
    </div>
  );
};

export default KeywordSummary;
